// drawTools.js
import { Draw } from 'ol/interaction.js';
import Select from 'ol/interaction/Select.js';
import LayerGroup from 'ol/layer/Group.js';
import VectorLayer from 'ol/layer/Vector.js';
import { singleClick } from 'ol/events/condition.js';
import VectorSource from 'ol/source/Vector.js';
import GeoJSON from 'ol/format/GeoJSON.js';
import { fromCircle } from 'ol/geom/Polygon.js';
import { Style, Stroke, Fill, Circle as CircleStyle } from 'ol/style.js';
import * as MapUtils from './mapUtils.js';
import { getMap } from './mapInstance.js';
import { deactivateDragZoom } from './Magnify.js';
import { deactivatePanMode } from './panUtils.js';
import * as config from './config.js';
import { secureFetch } from './securityUtil.js';
import { unByKey } from 'ol/Observable';
import { canUseAddressAidsDraw } from './addressAidsAvailability.js';
import { resetActiveCommandsOnly } from './resetApplication.js';
import { setActiveSidebarTool, setSidebarToolActive } from './dialogs.js';
import { setupDrawInteraction } from './addressSearch.js';
import { startDrawAutoPan, stopDrawAutoPan } from './autoPan.js';
import { attachRadiusDrawLabel, createRadiusDrawStyle } from './radiusDrawStyle.js';

const DRAW_AUTOPAN = {
  edgeThreshold: 64,
  panSpeedPx: 4
};


/** Interactions */
let selectInteraction = null;
let drawInteraction = null;
let lastSelectedFeature = null;
let allowedPCCodes = null;
let pcmFetchPromise = null;
let pointerMoveKey = null;
let escapeCancelBound = false;
let drawSessionActive = false;
let map = null;
let drawToolsInitialized = false;
let mapReadyListenerBound = false;

function resetAddressToolsPreservingLegend() {
  resetActiveCommandsOnly({
    preserveAddressAidsResults: true,
    legendSuspendMs: 2500
  });
}

function preserveLegendAroundAddressTool(action) {
  const messageEl = document.getElementById('message-text');
  const messageHtml = messageEl?.innerHTML;
  const restoreLegend = () => {
    if (messageEl?.isConnected && messageHtml !== undefined && messageHtml !== null) {
      messageEl.innerHTML = messageHtml;
    }
  };

  MapUtils.suppressBaseLegendUpdates?.(2500);
  try {
    action();
  } finally {
    restoreLegend();
    [50, 150, 350, 750, 1250, 2000].forEach(delay => {
      window.setTimeout(restoreLegend, delay);
    });
  }
}

function setAddressDialogActiveTool(selector) {
  if (selector) {
    setActiveSidebarTool('.draw-panel-toggle', { lock: true });
  } else {
    setSidebarToolActive('.draw-panel-toggle', false);
  }

  document
    .querySelectorAll('#draw-panel .draw-polygon, #draw-panel .draw-circle, #draw-panel .select-area')
    .forEach(button => {
      const active = Boolean(selector) && button.matches(selector);
      button.classList.toggle('active', active);
      button.classList.toggle('active-tool', active);
      button.classList.toggle('active-edit-vertex', active);
      button.setAttribute('aria-pressed', String(active));
      if (active) {
        button.style.setProperty('background', 'var(--sevenn-primary)', 'important');
        button.style.setProperty('background-color', 'var(--sevenn-primary)', 'important');
        button.style.setProperty('background-image', 'none', 'important');
        button.style.setProperty('border-color', 'var(--sevenn-primary)', 'important');
        button.style.setProperty('color', '#fff', 'important');
        button.style.setProperty('box-shadow', '0 8px 18px rgba(59, 57, 232, 0.24)', 'important');
      } else {
        button.style.removeProperty('background');
        button.style.removeProperty('background-color');
        button.style.removeProperty('background-image');
        button.style.removeProperty('border-color');
        button.style.removeProperty('color');
        button.style.removeProperty('box-shadow');
      }
    });
}

export let vecSource; // vector source exposed to other modules

function flattenLayers(layerList) {
  const out = [];
  layerList.forEach(layer => {
    out.push(layer);
    if (layer instanceof LayerGroup && typeof layer.getLayers === 'function') {
      const inside = layer.getLayers().getArray();
      out.push(...flattenLayers(inside));
    }
  });
  return out;
}

function getAllowedPCLayers() {
  if (!Array.isArray(allowedPCCodes)) return [];
  const all = flattenLayers(map.getLayers().getArray());
  return all.filter(l => {
    if (!(l instanceof VectorLayer)) return false;
    const t = l.get('title') || '';
    return allowedPCCodes.includes(t.trim().toUpperCase());
  });
}

function pointerMoveHandler(evt) {

  const map = getMap();
  if (!map) return;

  if (evt.dragging) return;
  const hit = map.hasFeatureAtPixel(evt.pixel, {
    hitTolerance: 4,
    layerFilter: lyr => getAllowedPCLayers().includes(lyr)
  });
  map.getViewport().style.cursor = hit ? 'pointer' : '';
}

const highlightStyle = new Style({
  stroke: new Stroke({ color: '#ff8800', width: 4 }),
  fill: new Fill({ color: 'rgba(255,136,0,0.25)' })
});

async function onFeatureSelect(evt) {
  const f = evt.selected && evt.selected[0];
  if (!f) return;

  // ✅ Toggle the clicked PC4/PC6 polygon itself
  MapUtils.toggleSelection(f);

  // Prepare payload
  const worldGeom = f.getGeometry().clone().transform('EPSG:3857', 'EPSG:4326');
  const payload = { geometry: new GeoJSON().writeGeometryObject(worldGeom) };

  try {
    const resp = await secureFetch(`${config.API_URL}spatial-search/polygon`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(payload),
      mode: 'cors'
    });

    const data = await resp.json();

    if (!data || data.type !== "FeatureCollection")
    {
      console.error("Invalid GeoJSON from backend:", data);
      setStatus('Backend returned invalid GeoJSON');
      return;
    }

    // Convert backend address results into OL Features
    const format = new GeoJSON();
    const features = format.readFeatures(data, {
      dataProjection: 'EPSG:3857',
      featureProjection: map.getView().getProjection()
    });

    // Toggle each address feature
    features.forEach(addr => MapUtils.toggleSelection(addr));

  } catch (err) {
    console.error('Error in PC spatial lookup', err);
  }

  // Exit select mode (optional — or keep active if you want multi-clicks)
  //deactivateSelectMode();
}

async function initAllowedPCCodes() {
  if (!pcmFetchPromise) {
    pcmFetchPromise = secureFetch(`${config.API_URL}spatial-search/pclayers`)
      .then(res => res.ok ? res.json() : [])
      .then(json => Array.isArray(json) ? json.map(x => String(x).trim().toUpperCase()) : [])
      .catch(err => { console.error('Error fetching PC codes:', err); return []; });
  }
  allowedPCCodes = await pcmFetchPromise;
  return allowedPCCodes;
}

async function activateSelectMode() {

  const map = getMap();
  if (!map) return;

  setCommand('Addresses selection using PC4/PC6');
  
  await initAllowedPCCodes();
  const allowedLayers = getAllowedPCLayers();
  if (!allowedLayers.length) return console.warn('No PC4/PC6 layers found');

  selectInteraction = new Select({
    condition: singleClick,
    layers: allowedLayers,
    hitTolerance: 4,
    style: highlightStyle
  });
  selectInteraction.on('select', onFeatureSelect);

  map.addInteraction(selectInteraction);
  //map.on('pointermove', pointerMoveHandler);
  pointerMoveKey = map.on('pointermove', pointerMoveHandler);
  MapUtils.setMapCursor('cell');
  document.querySelector('.select-area')?.classList.add('active');
  setAddressDialogActiveTool('.select-area');
}

function deactivateSelectMode() {

  const map = getMap();
  if (!map) return;

  if (selectInteraction) {
    map.removeInteraction(selectInteraction);
    selectInteraction = null;
  }

  if (pointerMoveKey) {
    unByKey(pointerMoveKey);
    pointerMoveKey = null;
  }

  MapUtils.setMapCursor('default');

  document.querySelector('.select-area')?.classList.remove('active');
  setAddressDialogActiveTool(null);
}

function deactivateAddressAidMode(reason = 'Cancelled', options = {}) {
  const currentMap = getMap();
  drawSessionActive = false;

  if (drawInteraction) {
    try {
      drawInteraction.abortDrawing?.();
    } catch (err) {
      console.warn("abortDrawing failed:", err);
    }
    currentMap?.removeInteraction(drawInteraction);
    drawInteraction = null;
  }
  stopDrawAutoPan();

  if (!options.preserveDrawnFeatures) {
    vecSource?.clear();
  }
  deactivateSelectMode();
  MapUtils.setMapCursor('default');
  MapUtils.setCommand?.('Ready');
  MapUtils.setStatus?.(reason);
  setAddressDialogActiveTool(null);
}

document.addEventListener('pc-selection-activating', () => {
  deactivateAddressAidMode('Cancelled');
});

document.addEventListener('application-reset-active-tools', event => {
  deactivateAddressAidMode('Ready', {
    preserveDrawnFeatures: !!event.detail?.preserveAddressAidsResults
  });
});

document.addEventListener('address-aids-unavailable', evt => {
  if (!drawInteraction && !selectInteraction && !drawSessionActive) return;
  deactivateAddressAidMode(evt.detail?.reason || 'Zoom in to use address selection.');
});

function notifyAddressAidsActivating() {
  document.dispatchEvent(new CustomEvent('address-aids-activating'));
}

function bindEscapeCancelOnce() {
  if (escapeCancelBound) return;

  document.addEventListener('keydown', e => {
    if (e.key !== 'Escape') return;

    const target = e.target;
    const tagName = target?.tagName?.toLowerCase?.() || '';
    if (['input', 'textarea', 'select'].includes(tagName) || target?.isContentEditable) {
      return;
    }

    if (drawInteraction || selectInteraction) {
      e.preventDefault();
      if (drawInteraction) {
        drawSessionActive = false;
        try {
          drawInteraction.abortDrawing();
        } catch (err) {
          console.warn("abortDrawing failed:", err);
        }
        map?.removeInteraction(drawInteraction);
        drawInteraction = null;
        vecSource?.clear();
      }

      if (selectInteraction) {
        drawSessionActive = false;
        deactivateSelectMode();
      }

      MapUtils.setMapCursor('default');
      setAddressDialogActiveTool(null);
      MapUtils.setStatus?.("Operation cancelled");
      MapUtils.setCommand?.("Ready");
    }
  });

  escapeCancelBound = true;
}

/**
 * Common handler for backend query + selection toggle
 */
async function processDrawnGeometry(geojsonGeom) {
  try {

    const resp = await secureFetch(`${config.API_URL}spatial-search/polygon`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ geometry: geojsonGeom }),
      mode: 'cors'
    });

    if (!resp.ok) throw new Error(`Backend returned ${resp.status}`);

    const data = await resp.json();

    const format = new GeoJSON();
    const features = format.readFeatures(data, {
      dataProjection: 'EPSG:4326',
      featureProjection: map.getView().getProjection()
    });

    // 🔍 Check current selection count before adding new features
    const prevSelectedCount = MapUtils.getSelectionCount ? MapUtils.getSelectionCount() : 0;

    // Toggle (magenta) per feature
    features.forEach(f => MapUtils.toggleSelection(f));

    const newCount = features.length;
    const totalSelected = MapUtils.getSelectionCount ? MapUtils.getSelectionCount() : prevSelectedCount + newCount;

    const msgParts = [];
    msgParts.push(`${newCount} new feature${newCount !== 1 ? 's' : ''} processed`);
    if (prevSelectedCount > 0) msgParts.push(`${prevSelectedCount} previously selected`);
    msgParts.push(`Total selected: ${totalSelected}`);

    MapUtils.setStatus(msgParts.join(' | '));


  } catch (err) {
    console.error('processDrawnGeometry failed:', err);
    MapUtils.setStatus('Spatial search failed.');
  }
}



async function drawPolygon() {

  if (!canUseAddressAidsDraw(map)) return;

  setCommand('Addresses selection using polygon');
  drawSessionActive = true;
  if (drawInteraction) map.removeInteraction(drawInteraction);
  
  const armDraw = () => {
    drawInteraction = new Draw({ source: vecSource, type: 'Polygon' });
    drawInteraction.on('drawstart', () => startDrawAutoPan(map, DRAW_AUTOPAN));
    map.addInteraction(drawInteraction);
    MapUtils.setMapCursor('crosshair');

    drawInteraction.once('drawend', async evt => {
      try {
        const geom = evt.feature.getGeometry().clone().transform('EPSG:3857', 'EPSG:4326');
        const geojsonGeom = new GeoJSON().writeGeometryObject(geom);
        await processDrawnGeometry(geojsonGeom);
      } catch (err) {
        console.error("Error while processing polygon draw:", err);
      } finally {
        stopDrawAutoPan();
        map.removeInteraction(drawInteraction);
        drawInteraction = null;
        MapUtils.setMapCursor('default');
        if (drawSessionActive) armDraw();
      }
    });
  };

  armDraw();
}

async function drawCircle() {
  
  if (!canUseAddressAidsDraw(map)) return;

  setCommand('Addresses selection using circle');
  drawSessionActive = true;
  if (drawInteraction) map.removeInteraction(drawInteraction);
  
  const armDraw = () => {
    drawInteraction = new Draw({
      source: vecSource,
      type: 'Circle',
      style: createRadiusDrawStyle()
    });
    attachRadiusDrawLabel(drawInteraction, map);
    drawInteraction.on('drawstart', () => startDrawAutoPan(map, DRAW_AUTOPAN));
    map.addInteraction(drawInteraction);
    MapUtils.setMapCursor('crosshair');

    drawInteraction.once('drawend', async evt => {
      try {
        const poly = fromCircle(evt.feature.getGeometry(), 64);
        const geo = poly.clone().transform('EPSG:3857', 'EPSG:4326');
        const geojsonGeom = new GeoJSON().writeGeometryObject(geo);
        await processDrawnGeometry(geojsonGeom);
      } catch (err) {
        console.error("Error while processing circle draw:", err);
      } finally {
        stopDrawAutoPan();
        map.removeInteraction(drawInteraction);
        drawInteraction = null;
        MapUtils.setMapCursor('default');
        if (drawSessionActive) armDraw();
      }
    });
  };

  armDraw();
}

// Attach button immediately when DOM loads
document.addEventListener("DOMContentLoaded", () => {

  const btn = document.getElementById("clear-address-selection");

  if (btn) {
    btn.addEventListener("click", () => {
      const messageEl = document.getElementById('message-text');
      const messageHtml = messageEl?.innerHTML;

      deactivateSelectMode();
      deactivateDragZoom();
      deactivatePanMode();

      MapUtils.cancelActiveTools("Selection cleared", { preserveMessage: true });
      MapUtils.clearSelection();

      if (messageEl) {
        messageEl.innerHTML = messageHtml;
        window.setTimeout(() => { if (messageEl.isConnected) messageEl.innerHTML = messageHtml; }, 100);
        window.setTimeout(() => { if (messageEl.isConnected) messageEl.innerHTML = messageHtml; }, 350);
      }

      setActiveSidebarTool('.draw-panel-toggle');
    });
  }

});

function initializeDrawTools(currentMap) {
  if (drawToolsInitialized || !currentMap) return;
  drawToolsInitialized = true;
  map = currentMap;
  vecSource = new VectorSource({ wrapX: false });

  const drawLayer = new VectorLayer({
    source: vecSource,
    style: MapUtils.getSelectionStyle()
  });

  map.addLayer(drawLayer);

  bindEscapeCancelOnce();

  document.querySelector('.select-area')?.addEventListener('click', () => {
    preserveLegendAroundAddressTool(() => {
      const shouldActivate = !selectInteraction;
      resetAddressToolsPreservingLegend();
      notifyAddressAidsActivating();
      deactivateDragZoom();
      deactivatePanMode();
      if (shouldActivate) activateSelectMode();
    });
  });

  document.querySelector('.draw-polygon')?.addEventListener('click', (event) => {
    if (!canUseAddressAidsDraw(map)) {
      event.preventDefault();
      event.stopImmediatePropagation();
      return;
    }

    preserveLegendAroundAddressTool(() => {
      deactivateSelectMode();
      resetAddressToolsPreservingLegend();
      notifyAddressAidsActivating();
      deactivateDragZoom();
      deactivatePanMode();
      setAddressDialogActiveTool('.draw-polygon');
      setupDrawInteraction('Polygon');
    });
  });

  document.querySelector('.draw-circle')?.addEventListener('click', (event) => {
    if (!canUseAddressAidsDraw(map)) {
      event.preventDefault();
      event.stopImmediatePropagation();
      return;
    }

    preserveLegendAroundAddressTool(() => {
      deactivateSelectMode();
      resetAddressToolsPreservingLegend();
      notifyAddressAidsActivating();
      deactivateDragZoom();
      deactivatePanMode();
      setAddressDialogActiveTool('.draw-circle');
      setupDrawInteraction('Circle');
    });
  });
}

export function setupDrawTools() {
  const currentMap = getMap();
  if (currentMap) {
    initializeDrawTools(currentMap);
    return;
  }

  if (mapReadyListenerBound) return;
  mapReadyListenerBound = true;
  document.addEventListener('map-ready', event => {
    initializeDrawTools(event.detail || getMap());
  }, { once: true });
}

